import React, { useState } from "react";
import { Check, X, ArrowRight, ExternalLink } from "lucide-react";
import { usePrice } from "@/lib/price";

const WHOP_CHECKOUT = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

/** Honest scoring. ✓ = wins this row, ✗ = loses. */
const categories = [
  {
    id: "jobs",
    label: "Job Board",
    rows: [
      "Pulls listings from employers' own ATS boards",
      "Matches listings to your resume / skills / location",
      "One-click application autofill",
      "Recruiter network + InMail",
      "Application pipeline tracker",
      "Genuinely free tier",
      "Resume builder + interview simulator included",
      "Hundreds of income playbooks included",
      "No weekly-billing upsell",
    ],
    jh: [true, true, false, false, true, true, true, true, true],
    jhPrice: "$7 once · lifetime",
    competitors: [
      {
        name: "LinkedIn Premium Career",
        price: "~$29.99 / mo",
        cells: [false, true, false, true, true, false, false, false, true],
      },
      {
        name: "Simplify+",
        price: "~$2,076 / yr equiv",
        cells: [false, true, true, false, true, true, false, false, false],
      },
      {
        name: "Teal Job Tracker",
        price: "~$29 / mo",
        cells: [false, true, false, false, true, true, false, false, true],
      },
    ],
  },
  {
    id: "resume",
    label: "Resume Builder",
    rows: [
      "Actually rebuilds your uploaded resume",
      "ATS-friendly builder",
      "Targets your resume to the specific posting",
      "Job tracker + Chrome extension",
      "Designer template / personal-site gallery",
      "Cover-letter builder",
      "Interview testing included (same price)",
      "Hundreds of income playbooks included",
      "Live job directory included",
      "Usable free tier",
      "One-time lifetime purchase option",
      "No weekly / 4-week billing cycles",
    ],
    jh: [
      true,
      true,
      true,
      true,
      false,
      false,
      true,
      true,
      true,
      true,
      true,
      true,
    ],
    jhPrice: "$7 once · lifetime",
    competitors: [
      {
        name: "Teal+",
        price: "~$29 / mo",
        cells: [false, true, true, true, false, true, false, false, false, true, false, true],
      },
      {
        name: "Rezi",
        price: "~$29 / mo",
        cells: [false, true, true, false, false, true, false, false, false, true, false, true],
      },
      {
        name: "Kickresume",
        price: "~$15 / mo",
        cells: [false, true, false, false, true, true, false, false, false, true, false, true],
      },
      {
        name: "Zety",
        price: "~$24 / 4-week",
        cells: [false, true, false, false, true, true, false, false, false, false, false, false],
      },
      {
        name: "Resume.io",
        price: "$2.95 trial → $24.95 / 4-week",
        cells: [false, true, false, false, true, true, false, false, false, false, false, false],
      },
      {
        name: "Novoresume",
        price: "~$19.99 / mo",
        cells: [false, true, false, false, true, true, false, false, false, true, false, true],
      },
      {
        name: "Enhancv",
        price: "~$24.99 / mo",
        cells: [false, true, true, false, true, true, false, false, false, true, false, true],
      },
      {
        name: "LiveCareer",
        price: "~$24.95 / 4-week (14-day trap)",
        cells: [false, true, false, false, false, true, false, false, false, false, false, false],
      },
      {
        name: "MyPerfectResume",
        price: "~$23.95 / 4-week (14-day trap)",
        cells: [false, true, false, false, false, true, false, false, false, false, false, false],
      },
      {
        name: "Resume Genius",
        price: "~$23.95 / 4-week (trap)",
        cells: [false, true, false, false, false, true, false, false, false, false, false, false],
      },
      {
        name: "Canva",
        price: "Free / $14.99 / mo Pro",
        cells: [false, false, false, false, true, false, false, false, false, true, false, true],
      },
      {
        name: "FlowCV",
        price: "Free / $9 / mo Pro",
        cells: [false, true, false, false, true, false, false, false, false, true, false, true],
      },
    ],
  },
  {
    id: "interview",
    label: "Interview Prep",
    rows: [
      "Still available June 2026",
      "Builds the test from a real job posting",
      "Study plan for the skills you're missing",
      "Speech & delivery analytics (pacing, fillers)",
      "Live in-interview answer copilot",
      "Resume builder included (same price)",
      "Playbooks + tool directory included",
      "Live job directory included",
      "Free tier",
    ],
    jh: [true, true, true, false, false, true, true, true, true],
    jhPrice: "$7 once · lifetime",
    competitors: [
      {
        name: "Final Round AI",
        price: "~$149 / mo",
        cells: [true, true, false, true, true, false, false, false, false],
      },
      {
        name: "Interview Warmup (Google)",
        price: "Free",
        cells: [true, false, false, true, false, false, false, false, true],
      },
      {
        name: "Yoodli",
        price: "Free / ~$20 / mo",
        cells: [true, false, false, true, false, false, false, false, true],
      },
      {
        name: "Huru",
        price: "~$20 / mo",
        cells: [true, true, false, true, false, false, false, false, false],
      },
    ],
  },
];

function CellMark({ on, side }) {
  // side: "jh" (gold) or "vs" (muted)
  const base =
    "inline-flex items-center justify-center w-9 h-9 border-2 border-[#0a0a0a] check-pop";
  if (on) {
    return side === "jh" ? (
      <span className={`${base} bg-[#FFE600] text-[#0a0a0a]`}>
        <Check size={18} strokeWidth={3} />
      </span>
    ) : (
      <span className={`${base} bg-[#00FF66] text-[#0a0a0a]`}>
        <Check size={18} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span
      className={`${base} bg-white text-[#0a0a0a]/35`}
      aria-label="not included"
    >
      <X size={18} strokeWidth={3} />
    </span>
  );
}

export default function Comparison() {
  const [catIdx, setCatIdx] = useState(0);
  const [vsIdx, setVsIdx] = useState(0);
  const cat = categories[catIdx];
  const vs = cat.competitors[vsIdx];
  const { price } = usePrice();

  const onCat = (i) => {
    setCatIdx(i);
    setVsIdx(0);
  };

  return (
    <section
      id="cmp"
      data-testid="comparison"
      className="border-b-4 border-[#0a0a0a] bg-[#FFE600]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="mb-10 md:mb-14 reveal">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            ONE {price} REPLACES YOUR WHOLE STACK
          </div>
          <h2 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] md:leading-[0.92] max-w-4xl">
            One directory vs{" "}
            <span className="bg-[#FF3B30] text-white px-2">
              a dozen scattered tabs.
            </span>
          </h2>
          <p className="font-mono-body mt-5 max-w-2xl text-[#0a0a0a]/80 text-base">
            Switch category, then pick the tool you&apos;re comparing.
            JobHacki is pinned on the left. Honest scoring — if a competitor
            genuinely wins a row, the ✓ is theirs.
          </p>
        </div>

        {/* Category tabs */}
        <div
          data-testid="cmp-category-tabs"
          className="flex flex-wrap gap-2 md:gap-3 mb-3 md:mb-4 reveal"
        >
          {categories.map((c, i) => (
            <button
              key={c.id}
              data-testid={`cmp-cat-${c.id}`}
              onClick={() => onCat(i)}
              className={`hover-pop inline-flex items-center gap-2 border-4 border-[#0a0a0a] font-accent text-sm md:text-base px-4 md:px-5 py-2.5 md:py-3 ${
                i === catIdx
                  ? "bg-[#0a0a0a] text-[#FFE600] shadow-brutal-sm"
                  : "bg-white text-[#0a0a0a]"
              }`}
            >
              <span className="font-mono-body text-[10px] md:text-xs opacity-70">
                0{i + 1}
              </span>
              {c.label.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Competitor tabs */}
        <div
          data-testid="cmp-vs-tabs"
          className="flex flex-wrap gap-2 mb-6 md:mb-8 reveal"
        >
          {cat.competitors.map((c, i) => (
            <button
              key={c.name}
              data-testid={`cmp-vs-${i}`}
              onClick={() => setVsIdx(i)}
              className={`font-accent text-[11px] md:text-xs px-3 py-2 border-2 border-[#0a0a0a] transition-colors ${
                i === vsIdx
                  ? "bg-[#FF3B30] text-white"
                  : "bg-white text-[#0a0a0a]/80 hover:bg-[#0a0a0a] hover:text-[#FFE600]"
              }`}
            >
              VS {c.name.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Table */}
        <div
          data-testid="cmp-table"
          className="bg-white border-4 border-[#0a0a0a] shadow-brutal-lg reveal"
        >
          {/* Sticky header row */}
          <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_180px_180px] bg-[#0a0a0a] text-[#FFE600] sticky top-[72px] z-30 border-b-4 border-[#0a0a0a]">
            <div className="px-3 md:px-5 py-3 md:py-4 font-accent text-[10.5px] md:text-xs">
              CAPABILITY
            </div>
            <div className="px-2 md:px-3 py-3 md:py-4 text-center bg-[#FFE600] text-[#0a0a0a] border-l-4 border-[#0a0a0a]">
              <div className="font-display text-sm md:text-lg leading-none">
                JOBHACKI
              </div>
              <div className="font-mono-body text-[9px] md:text-[10px] mt-0.5 opacity-75">
                ALL-ACCESS
              </div>
            </div>
            <div className="px-2 md:px-3 py-3 md:py-4 text-center border-l-2 border-[#FFE600]/40">
              <div
                key={vs.name}
                data-testid="cmp-vs-name"
                className="font-display text-[11px] md:text-base leading-tight"
              >
                {vs.name}
              </div>
              <div className="font-mono-body text-[9px] md:text-[10px] mt-0.5 opacity-75">
                COMPETITOR
              </div>
            </div>
          </div>

          {/* Rows */}
          {cat.rows.map((row, i) => {
            const jhWin = cat.jh[i];
            const vsWin = vs.cells[i];
            return (
              <div
                key={row}
                data-testid={`cmp-row-${i}`}
                className={`grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_180px_180px] items-center border-t border-[#0a0a0a]/10 ${
                  i % 2 ? "bg-[#FDFBF7]" : "bg-white"
                }`}
              >
                <div className="px-3 md:px-5 py-3 md:py-4 font-mono-body text-[12.5px] md:text-sm leading-snug pr-2">
                  {row}
                </div>
                <div className="px-2 md:px-3 py-3 md:py-4 flex items-center justify-center bg-[#FFE600]/20 border-l-4 border-[#0a0a0a]">
                  <CellMark on={jhWin} side="jh" />
                </div>
                <div className="px-2 md:px-3 py-3 md:py-4 flex items-center justify-center border-l-2 border-[#0a0a0a]/10">
                  <CellMark on={vsWin} side="vs" />
                </div>
              </div>
            );
          })}

          {/* Price row */}
          <div
            data-testid="cmp-row-price"
            className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_180px_180px] items-stretch border-t-4 border-[#0a0a0a] bg-[#0a0a0a] text-white"
          >
            <div className="px-3 md:px-5 py-4 font-accent text-xs md:text-sm flex items-center text-[#FFE600]">
              PRICE
            </div>
            <div className="px-2 md:px-3 py-4 flex flex-col items-center justify-center bg-[#FFE600] text-[#0a0a0a] border-l-4 border-[#0a0a0a] text-center">
              <div className="font-display text-base md:text-2xl leading-tight">
                {price}
              </div>
              <div className="font-mono-body text-[10px] md:text-[11px] mt-0.5">
                once · lifetime
              </div>
            </div>
            <div className="px-2 md:px-3 py-4 flex items-center justify-center border-l-2 border-[#FFE600]/40 text-center">
              <div
                data-testid="cmp-vs-price"
                className="font-mono-body text-[11px] md:text-[13px] text-white/90 leading-tight"
              >
                {vs.price}
              </div>
            </div>
          </div>
        </div>

        {/* Summary + caption */}
        <div className="mt-6 md:mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 reveal">
          <div className="font-display text-[18px] md:text-2xl">
            JobHacki:{" "}
            <span className="bg-[#0a0a0a] text-[#FFE600] px-2">
              {price} once, lifetime
            </span>{" "}
            — everything.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={WHOP_CHECKOUT}
              data-testid="cmp-cta-lock"
              target="_blank"
              rel="noopener noreferrer"
              className="hover-pop inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFE600] border-4 border-[#0a0a0a] px-5 py-3 font-accent text-sm md:text-base shadow-brutal-sm"
            >
              Unlock everything — {price}
              <ExternalLink size={14} strokeWidth={3} />
            </a>
            <a
              href="/login"
              data-testid="cmp-cta-free"
              className="hover-pop inline-flex items-center gap-2 bg-white text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3 font-accent text-sm md:text-base shadow-brutal-sm"
            >
              Start free
              <ArrowRight size={14} strokeWidth={3} />
            </a>
          </div>
        </div>
        <div className="mt-3 font-mono-body text-[11px] text-[#0a0a0a]/55">
          Prices verified June 2026. Competitor prices change frequently —
          last checked: 06/2026.
        </div>
      </div>
    </section>
  );
}
